<template>
  <div :class="{ 'connect-mt-account-view': !isOnboarding }">
    <template v-if="showSyncStatus && isOnboarding">
      <SyncStatus :loading="loading" :success="syncSuccess" :platform-logo="platformLogo" :total-trades="totalTrades" />
    </template>
    <div :class="{ 'ca-container': !isOnboarding }" v-else>
      <img width="100" :src="platformLogo" alt="Metatrader.png" class='mb-5' />
      <h2 class='mb-3'>MetaTrader Auto-Sync</h2>
      <div>
        <p class='mb-10'>{{ $t("text1") }}</p>
        <p v-if="false" class='mb-10'>{{ $t("text2") }}</p>
      </div>
      <v-form ref="form">
        <v-select :label="$t('versionLabel')" :items="versions" item-text="text" item-value="value"
          v-model="currentVersion" :rules="versionRules" outlined @change="onVersionChange" />
        <v-autocomplete :label="$t('serverLabel')" :items="currentServers" item-text="name" item-value="name"
          v-model="server" :rules="serverRules" outlined return-object no-data-text="No servers found" />
        <div class="d-flex align-start justify-start">
          <v-text-field class="basic-element" :label="$t('form.mtLogin.label')" :rules="mtLoginRules" v-model="mtLogin"
            outlined type="number" />
        </div>
        <div class="d-flex align-start justify-start">
          <v-text-field class="basic-element" :label="$t('form.mtInvestorPassword.label')"
            :rules="mtInvestorPasswordRules" v-model="mtInvestorPassword" outlined />
        </div>
        <div class="password-disclaimer">
          <p class="text-caption accent text-start" style='color: var(--v-mediumGray-base) !important'>{{
            $t('form.mtInvestorPassword.disclaimer') }}</p>
        </div>
      </v-form>
      <portal to="onboarding-actions" v-if="!showSyncStatus && isOnboarding">
        <v-btn @click="submitForm" :disabled="!isFormValid || loading" :loading="loading" class="primary-btn">
          {{ $t("submit") }}</v-btn>
      </portal>
      <v-btn @click="submitForm" v-if="!isOnboarding" :disabled="!isFormValid || loading" :loading="loading"
        class="primary-btn">
        {{ $t("submit") }}</v-btn>
    </div>
    <v-overlay :dark="false" v-model="modalSyncedV" @click:outside="closeModal">
      <v-alert :value="modalSyncedV" :title="modalTitle" dismissible :type="modalType" @input="closeModal"
        @click:close="closeModal">
        {{ modalText }}
        <template v-slot:close>
          <v-btn style="background-color: transparent !important;" icon small @click="closeModal">
            <v-icon style="color: #FFF !important;">fa-regular fa-times</v-icon>
          </v-btn>
        </template>
      </v-alert>
    </v-overlay>
    <LoadingOverlay :loading="loading" />
  </div>
</template>

<script>
import metaTraderCredentials from "../../repositories/metaTraderCredentials";
import brokerAccountRepository from "../../repositories/brokerAccount";
import authRepository from "../../repositories/auth";
import LoadingOverlay from "../../components/LoadingOverlay.vue";
import SyncStatus from '../../components/PlatformSync/SyncStatus.vue';

export default {
  name: "ConnectAccount",
  components: {
    LoadingOverlay,
    SyncStatus
  },
  props: {
    isOnboarding: {
      type: Boolean,
      default: false
    },
    syncLoading: {
      type: Function,
      default: () => { }
    }
  },
  computed: {
    isFormValid() {
      return !!(
        this.mtLogin &&
        this.mtInvestorPassword &&
        this.currentVersion &&
        this.server
      );
    },
    currentServers() {
      return this.currentVersion === 'MT4' ? this.mt4Servers : this.mt5Servers;
    }
  },
  data() {
    return {
      mt5Servers: [],
      mt4Servers: [],
      modalText: "",
      modalTitle: "",
      modalType: "",
      accountType: "DEMO",
      accountTypes: [
        { text: "Real", value: "LIVE" },
        { text: "Demo", value: "DEMO" },
      ],
      accountTypeRules: [(v) => !!v || this.$t("validations.accountTypeMandatory")],
      modalSyncedV: false,
      loading: false,
      totalTrades: 0,
      versions: [
        { text: "MetaTrader 4", value: "MT4" },
        { text: "MetaTrader 5", value: "MT5" },
      ],
      currentVersion: "MT5",
      platformLogo: "https://hub.swiset.com/wp-content/uploads/2024/02/MetaTrader-Swiset.png",
      server: "",
      versionDisabled: true,
      showSyncStatus: false,
      syncSuccess: false,
      errorMsj: "",
      mtLogin: "",
      mtInvestorPassword: "",
      versionRules: [(v) => !!v || this.$t("validations.versionMandatory")],
      serverRules: [(s) => !!s || this.$t("validations.serverMandatory")],
      mtLoginRules: [(mtl) => !!mtl || this.$t("form.mtLogin.mandatory")],
      mtInvestorPasswordRules: [
        (mtl) => !!mtl || this.$t("form.mtInvestorPassword.mandatory"),
      ],
      hostRules: [(h) => !!h || this.$t("validations.hostMandatory")],
      user: {},
    };
  },
  async mounted() {
    this.loading = true;
    await brokerAccountRepository.serverDetailsByVersion("MT4").then((res) => {
      this.mt4Servers = res.data;
    });
    await brokerAccountRepository.serverDetailsByVersion("MT5").then((res) => {
      this.mt5Servers = res.data;
    });
    this.user = (await authRepository.getUser()).data;
    this.loading = false;
  },
  methods: {
    onVersionChange() {
      // Reset server selection when version changes
      this.server = "";
    },
    closeModal() {
      this.modalSyncedV = false;
      this.showSyncStatus = false;
      this.errorMsj = "";
      this.modalText = "";
      this.modalTitle = "";
      this.modalType = "";
    },
    async submitForm() {
      this.showSyncStatus = true;
      this.loading = true;
      this.syncLoading(true);

      const payload = {
        host: this.currentVersion === 'MT4' ? this.server.name : this.server.host,
        serverName: this.currentVersion === 'MT4' ? this.server.name : "",
        user: {
          id: this.user.id,
        },
        login: this.mtLogin,
        password: this.mtInvestorPassword,
        brokerId: this.server.broker.id,
        accountType: this.server.type,
        version: this.currentVersion,
      };

      // Add safety checks
      if (!metaTraderCredentials) {
        // eslint-disable-next-line no-console
        console.error('MetaTrader credentials repository is undefined');
        this.loading = false;
        this.syncLoading(false);
        return;
      }

      try {
        let response;
        response = await metaTraderCredentials.createCredentialsMT5.call(metaTraderCredentials, payload);

        if (this.isOnboarding) {
          this.$emit('sync-success', response.data);
          this.syncSuccess = true;
          this.totalTrades = response.data.total_trades;
        }
        this.modalType = "success";

      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('API Error:', error);
        this.modalType = "error";
        if (this.isOnboarding) {
          this.$emit('sync-error', error);
          this.syncSuccess = false;
        }

        // Extract errorMsg from API response if available
        let apiErrorMsg = '';
        if (error.response && error.response.data && error.response.data.errorMsg) {
          apiErrorMsg = error.response.data.errorMsg;
          this.errorMsj = `\nAPI Error: ${apiErrorMsg}`;
        } else if (error.response && error.response.data && typeof error.response.data === 'string') {
          apiErrorMsg = error.response.data;
          this.errorMsj = `\nAPI Error: ${apiErrorMsg}`;
        } else {
          // Only show generic error info if no specific API error message
          this.errorMsj = `\nError Code: ${error.response?.status || 'Unknown'}\nError: ${error.message || 'Connection failed'}`;
        }
      } finally {
        this.loading = false;
        this.syncLoading(false);
        if (!this.isOnboarding) {
          this.modalText =
            this.$i18n.messages[this.$i18n.locale]["modal"][this.modalType][
            "text"
            ] + this.errorMsj;
          this.modalTitle =
            this.$i18n.messages[this.$i18n.locale]["modal"][this.modalType][
            "title"
            ];
          this.modalSyncedV = true;
        }
      }
    },
  },
};
</script>



<style lang="scss">
.connect-mt-account-view {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 95vh;

  @media (max-width: 768px) {
    padding: 40px;
  }

  .ca-container {
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;

    img {
      margin-bottom: 20px;
      margin-left: auto;
      margin-right: auto;
    }

    h1 {
      margin-top: 20px;
      margin-bottom: 20px;

      @media (max-width: 768px) {
        font-size: 18px;
      }
    }

    p {
      @media (max-width: 768px) {
        font-size: 14px;
        text-align: start;
      }

      font-size: 16px;
      text-align: center;
      margin-bottom: 10px;
    }

    .v-form {
      margin-top: 20px;

      input {
        @media (max-width: 768px) {
          font-size: 12px;
        }
      }

      label {
        @media (max-width: 768px) {
          font-size: 14px;
        }
      }
    }

    .primary-btn {
      @media (max-width: 768px) {
        width: 65%;
      }
    }
  }
}

.password-disclaimer {
  margin-top: -8px;
  margin-bottom: 16px;
}

/* Alert modal width constraints */
.v-overlay .v-alert {
  max-width: 90vw;
  width: auto;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

@media (min-width: 768px) {
  .v-overlay .v-alert {
    max-width: 600px;
  }
}
</style>
<i18n>
  {
  "en": {
    "serverLabel":"Select server",
    "hostLabel":"Host",
    "accountTypeLabel":"Account type",
    "versionLabel":"MetaTrader version",
    "text1":"Sync your MetaTrader account without any EA required",
    "text2":"Be part of our betatesting for MetaTrader 4 & MetaTrader 5 auto-sync engine",
    "broughtToYou": "Brought to you by",
    "submit":"Connect account",
    "exnessDialog": {
      "title": "Exness Account Detected",
      "message": "We've detected you're trying to connect an Exness account. For a better experience and more features, we recommend using our dedicated Exness integration page.",
      "redirect": "Go to Exness Integration",
      "continue": "Continue with MT5 anyway"
    },
    "modal":{
      "success":{
        "title":"Account synced",
        "text":"Your MetaTrader account was successful synced"
      },
      "error":{
        "title":"Error",
        "text":"There was a problem connecting your MetaTrader account. Please try again or contact support."
      }
    },
    "steps": {
      "1": {
        "headOne": "Just few seconds to",
        "headTwo": "fit your trader experience",
        "btnContinue": "Continue »"
      },
      "2": {
        "headOne": "Almost done to",
        "headTwo": "lift your trading to a top level",
        "btnBack": "« Back",
        "btnContinue": "Continue »"
      },
      "3": {
        "headOne": "Great!",
        "headTwo": "Connect & Sync your {partnerShortName} Account",
        "chip": "Trades will be automatically synced",
        "btnContinue": "Connect Account »"
      }
    },
    "categories": {
      "traderTypes": {
        "iDontKnow": "I don't know",
        "highFrequencyTrader": "High-Frequency Trader"
      },
      "experiences": {
        "learning": "Just learning trading",
        "demo": "Trading on demo",
        "realNoProfit": "Trading on real without profit",
        "profitSixMonths": "Profitable on real less than 6 months",
        "profitConsistent": "Profitable on real more than 6 months"
      }
    },
    "form": {
      "firstName": {
        "label": "What's your first name?",
        "mandatory": "First name is required"
      },
      "lastName": {
        "label": "What's your last name?",
        "mandatory": "Last name is required"
      },
      "username": {
        "label": "Create a username",
        "mandatory": "Username is required",
        "maxLength": "Username should have less than 35 characters.",
        "not1": "Not '@' allowed",
        "not2": "Not & allowed",
        "not3": "Not spaces allowed",
        "existingIssue": "Username already exists.\nPlease try with another one."
      },
      "country": {
        "label": "Where do you live?",
        "mandatory": "Country is mandatory"
      },
      "typeOfTrading": {
        "label": "What's your favorite type of trading?",
        "mandatory": "Trading style is mandatory"
      },
      "experience": {
        "label": "What's your experience trading?",
        "mandatory": "Experience is mandatory"
      },
      "mtServer": {
        "label": "Select your {partnerShortName} MT4 Server",
        "mandatory": "MetaTrader server is mandatory",
        "explanation": {
          "title": "How to find out your<br>{partnerShortName} MT4 Server?",
          "description": "Over your Navigator > YourBrokerName > Accounts"
        }
      },
      "mtLogin": {
        "label": "Account Login",
        "mandatory": "MetaTrader login is mandatory",
        "explanation": {
          "title": "How to find out your<br>MT4 Account Login?",
          "description": "Over your Navigator > YourBrokerName > Accounts"
        }
      },
      "mtInvestorPassword": {
        "label": "Investor Password",
        "mandatory": "Password is mandatory",
        "explanation": {
          "title": "How to find out your<br>MT4 Password?",
          "description": "Click on Tools > Options > Server > Change Password > Change Investor (read only) password."
        },
        "disclaimer": "Important: Please ensure you enter your Investor Password only, not your Master or Trade Password. Swiset requires read-only access and is not responsible for any issues arising from providing incorrect password types."
      }
    },
    "formIssue": "Error with your data.\nPlease review each field.",
    "commonIssue": "An error occurred.\nTry again.",
    "stepProgress": "<b>Step {current}</b> from {total}",
    "validations":{
      "serverMandatory":"Server is mandatory",
      "hostMandatory":"Host is mandatory",
      "accountTypeMandatory":"Account type is mandatory"
    }
  },
  "es": {
    "serverLabel":"Servidor",
    "accountTypeLabel":"Tipo de cuenta",
    "hostLabel":"Host",
    "submit":"Conectar cuenta",
    "text1":"Sincronice su cuenta de MetaTrader",
    "text2":"Se parte de nuestro betatesting del motor Auto-Sync para MetaTrader 4 y 5 ",
    "versionLabel":"Versión MetaTrader",
    "broughtToYou": "Traído a ti por",
    "exnessDialog": {
      "title": "Cuenta Exness Detectada",
      "message": "Hemos detectado que estás intentando conectar una cuenta de Exness. Para una mejor experiencia y más funcionalidades, te recomendamos usar nuestra página de integración dedicada para Exness.",
      "redirect": "Ir a la Integración de Exness",
      "continue": "Continuar con MT5 de todas formas"
    },
    "modal":{
      "success":{
        "title":"Cuenta sincronizada",
        "text":"Tu cuenta de  MetaTrader se sincronizó con éxito"
      },
      "error":{
        "title":"Error",
        "text":"Hubo un problema conectando tu cuenta de MetaTrader. Inténtalo de nuevo o contacta con soporte."
      }
    },
    "steps": {
      "1": {
        "headOne": "Pocos segundos para",
        "headTwo": "personalizar tu experiencia de trader",
        "btnContinue": "Continuar »"
      },
      "2": {
        "headOne": "Casi listo para",
        "headTwo": "elevar tu trading a nivel top",
        "btnBack": "« Atrás",
        "btnContinue": "Continuar »"
      },
      "3": {
        "headOne": "¡Genial!",
        "headTwo": "Conecta & Sincroniza tu cuenta {partnerShortName}",
        "chip": "Trades serán automáticamente sincronizados",
        "btnContinue": "Conectar Cuenta »"
      }
    },
    "categories": {
      "traderTypes": {
        "iDontKnow": "No lo sé",
        "highFrequencyTrader": "Trader de Alta-Frecuencia"
      },
      "experiences": {
        "learning": "Sólo aprendiendo de trading",
        "demo": "Trading en demo",
        "realNoProfit": "Trading en real sin beneficio",
        "profitSixMonths": "Rentable en real por menos de 6 meses",
        "profitConsistent": "Rentable en real por más de 6 meses"
      }
    },
    "form": {
      "firstName": {
        "label": "¿Cuál es tu nombre?",
        "mandatory": "Nombre es obligatorio"
      },
      "lastName": {
        "label": "¿Cuál es tu apellido?",
        "mandatory": "Apellido es obligatorio"
      },
      "username": {
        "label": "Crea un nombre de usuario",
        "mandatory": "Nombre de usuario es obligatorio",
        "maxLength": "Nombre de usuario debe tener máximo 35 caracteres.",
        "not1": "No '@' permitidos",
        "not2": "No & permitidos",
        "not3": "No espacios permitidos",
        "existingIssue": "Nombre de usuario ya existe.\nPor favor intenta con otro."
      },
      "country": {
        "label": "¿Dónde vives?",
        "mandatory": "País es obligatorio"
      },
      "typeOfTrading": {
        "label": "¿Cúal es tu estilo de trading favorito?",
        "mandatory": "Estilo de trading es obligatorio"
      },
      "experience": {
        "label": "¿Cuál es tu experiencia en el trading?",
        "mandatory": "Experiencia es obligatoria"
      },
      "mtServer": {
        "label": "Selecciona tu Servidor {partnerShortName} MT4",
        "mandatory": "Servidor MetaTrader es obligatorio",
        "explanation": {
          "title": "¿Cómo encontrar tu<br>Servidor MT4 de {partnerShortName}?",
          "description": "Sobre Navegador  > YourBrokerName > Cuentas"
        }
      },
      "mtLogin": {
        "label": "Login de Cuenta",
        "mandatory": "Cuenta MetaTrader es obligatoria",
        "explanation": {
          "title": "¿Cómo encontrar tu<br>Login de Cuenta MT4?",
          "description": "Sobre Navegador > YourBrokerName > Cuentas"
        }
      },
      "mtInvestorPassword": {
        "label": "Contraseña de Inversionista",
        "mandatory": "Contraseña es obligatoria",
        "explanation": {
          "title": "¿Cómo encontrar tu<br>Contraseña MT4?",
          "description": "Click en Herramientas > Opciones > Servidor > Cambiar Contraseña > Cambiar contraseña (sólo lectura) de inversionista."
        },
        "disclaimer": "Importante: Asegúrese de ingresar solo su Contraseña de Inversionista, no su Contraseña Maestra o de Operaciones. Swiset requiere acceso de solo lectura y no se hace responsable por problemas derivados de proporcionar tipos de contraseña incorrectos."
      }
    },
    "formIssue": "Error con tu información.\nPor favor revisa cada campo.",
    "commonIssue": "Ocurrió un error.\nIntenta de nuevo.",
    "stepProgress": "<b>Paso {current}</b> de {total}",
    "validations":{
      "serverMandatory":"El servidor es obligatiorio",
      "hostMandatory":"Host es obligatorio",
      "accountTypeMandatory":"Tipo de cuenta es obligatorio"
    }
  },
  "zh": {
  "serverLabel": "选择服务器",
  "hostLabel": "主机",
  "accountTypeLabel": "账户类型",
  "versionLabel": "MetaTrader 版本",
  "text1": "无需任何 EA 即可同步您的 MetaTrader 账户",
  "text2": "加入我们的 MetaTrader 4 和 MetaTrader 5 自动同步引擎的测试",
  "broughtToYou": "由以下机构提供",
  "submit": "连接账户",
  "exnessDialog": {
    "title": "检测到 Exness 账户",
    "message": "我们检测到您正在尝试连接 Exness 账户。为了获得更好的体验和更多功能，我们建议您使用专用的 Exness 集成页面。",
    "redirect": "前往 Exness 集成页面",
    "continue": "仍然使用 MT5 继续"
  },
  "modal": {
    "success": {
      "title": "账户已同步",
      "text": "您的 MetaTrader 账户已成功同步"
    },
    "error": {
      "title": "错误",
      "text": "连接您的 MetaTrader 账户时出现问题。请重试或联系支持。"
    }
  },
  "steps": {
    "1": {
      "headOne": "只需几秒钟即可",
      "headTwo": "匹配您的交易体验",
      "btnContinue": "继续 »"
    },
    "2": {
      "headOne": "几乎完成，准备",
      "headTwo": "将您的交易提升到新水平",
      "btnBack": "« 返回",
      "btnContinue": "继续 »"
    },
    "3": {
      "headOne": "太棒了！",
      "headTwo": "连接并同步您的 {partnerShortName} 账户",
      "chip": "交易将自动同步",
      "btnContinue": "连接账户 »"
    }
  },
  "categories": {
    "traderTypes": {
      "iDontKnow": "我不知道",
      "highFrequencyTrader": "高频交易者"
    },
    "experiences": {
      "learning": "刚开始学习交易",
      "demo": "在模拟账户交易",
      "realNoProfit": "真实账户交易但尚无盈利",
      "profitSixMonths": "真实账户盈利少于 6 个月",
      "profitConsistent": "真实账户盈利超过 6 个月"
    }
  },
  "form": {
    "firstName": {
      "label": "您的名字是什么？",
      "mandatory": "名字是必填项"
    },
    "lastName": {
      "label": "您的姓氏是什么？",
      "mandatory": "姓氏是必填项"
    },
    "username": {
      "label": "创建一个用户名",
      "mandatory": "用户名是必填项",
      "maxLength": "用户名应少于 35 个字符。",
      "not1": "不允许使用“@”",
      "not2": "不允许使用“&”",
      "not3": "不允许使用空格",
      "existingIssue": "用户名已存在。\n请尝试另一个。"
    },
    "country": {
      "label": "您住在哪里？",
      "mandatory": "国家是必填项"
    },
    "typeOfTrading": {
      "label": "您最喜欢的交易类型是什么？",
      "mandatory": "交易风格是必填项"
    },
    "experience": {
      "label": "您的交易经验如何？",
      "mandatory": "经验是必填项"
    },
    "mtServer": {
      "label": "选择您的 {partnerShortName} MT4 服务器",
      "mandatory": "MetaTrader 服务器是必填项",
      "explanation": {
        "title": "如何找到您的<br>{partnerShortName} MT4 服务器？",
        "description": "在导航器 > 您的经纪商名称 > 账户中查看"
      }
    },
    "mtLogin": {
      "label": "账户登录",
      "mandatory": "MetaTrader 登录是必填项",
      "explanation": {
        "title": "如何找到您的<br>MT4 登录账户？",
        "description": "在导航器 > 您的经纪商名称 > 账户中查看"
      }
    },
    "mtInvestorPassword": {
      "label": "投资者密码",
      "mandatory": "密码是必填项",
      "explanation": {
        "title": "如何找到您的<br>MT4 密码？",
        "description": "点击 工具 > 选项 > 服务器 > 更改密码 > 更改投资者（只读）密码。"
      },
      "disclaimer": "重要提示：请确保只输入投资者密码，而不是主密码或交易密码。Swiset 仅需读取权限，不对因提供错误密码类型而导致的问题负责。"
    }
  },
  "formIssue": "您的数据有误。\n请检查每个字段。",
  "commonIssue": "发生错误。\n请重试。",
  "stepProgress": "<b>第 {current} 步</b>，共 {total} 步",
  "validations": {
    "serverMandatory": "服务器是必填项",
    "hostMandatory": "主机是必填项",
    "accountTypeMandatory": "账户类型是必填项"
  }
  },
  "pt": {
  "serverLabel": "Selecionar servidor",
  "hostLabel": "Host",
  "accountTypeLabel": "Tipo de conta",
  "versionLabel": "Versão do MetaTrader",
  "text1": "Sincronize sua conta MetaTrader sem precisar de nenhum EA",
  "text2": "Participe do nosso beta testing para o mecanismo de auto-sincronização do MetaTrader 4 e MetaTrader 5",
  "broughtToYou": "Apresentado por",
  "submit": "Conectar conta",
  "exnessDialog": {
    "title": "Conta Exness Detectada",
    "message": "Detectamos que você está tentando conectar uma conta Exness. Para uma melhor experiência e mais funcionalidades, recomendamos usar nossa página dedicada de integração com a Exness.",
    "redirect": "Ir para Integração com Exness",
    "continue": "Continuar com o MT5 mesmo assim"
  },
  "modal": {
    "success": {
      "title": "Conta sincronizada",
      "text": "Sua conta MetaTrader foi sincronizada com sucesso"
    },
    "error": {
      "title": "Erro",
      "text": "Houve um problema ao conectar sua conta MetaTrader. Por favor, tente novamente ou entre em contato com o suporte."
    }
  },
  "steps": {
    "1": {
      "headOne": "Apenas alguns segundos para",
      "headTwo": "ajustar sua experiência como trader",
      "btnContinue": "Continuar »"
    },
    "2": {
      "headOne": "Quase pronto para",
      "headTwo": "elevar seu trading a um novo nível",
      "btnBack": "« Voltar",
      "btnContinue": "Continuar »"
    },
    "3": {
      "headOne": "Ótimo!",
      "headTwo": "Conecte e sincronize sua conta {partnerShortName}",
      "chip": "As negociações serão sincronizadas automaticamente",
      "btnContinue": "Conectar Conta »"
    }
  },
  "categories": {
    "traderTypes": {
      "iDontKnow": "Não sei",
      "highFrequencyTrader": "Trader de Alta Frequência"
    },
    "experiences": {
      "learning": "Apenas aprendendo a negociar",
      "demo": "Negociando em conta demo",
      "realNoProfit": "Negociando em conta real sem lucro",
      "profitSixMonths": "Lucrativo em conta real por menos de 6 meses",
      "profitConsistent": "Lucrativo em conta real por mais de 6 meses"
    }
  },
  "form": {
    "firstName": {
      "label": "Qual é o seu primeiro nome?",
      "mandatory": "O primeiro nome é obrigatório"
    },
    "lastName": {
      "label": "Qual é o seu sobrenome?",
      "mandatory": "O sobrenome é obrigatório"
    },
    "username": {
      "label": "Crie um nome de usuário",
      "mandatory": "O nome de usuário é obrigatório",
      "maxLength": "O nome de usuário deve ter menos de 35 caracteres.",
      "not1": "Não é permitido '@'",
      "not2": "Não é permitido &",
      "not3": "Não são permitidos espaços",
      "existingIssue": "Nome de usuário já existe.\nPor favor, tente outro."
    },
    "country": {
      "label": "Onde você mora?",
      "mandatory": "País é obrigatório"
    },
    "typeOfTrading": {
      "label": "Qual é o seu tipo de negociação preferido?",
      "mandatory": "Estilo de negociação é obrigatório"
    },
    "experience": {
      "label": "Qual é sua experiência com negociação?",
      "mandatory": "Experiência é obrigatória"
    },
    "mtServer": {
      "label": "Selecione seu servidor {partnerShortName} MT4",
      "mandatory": "Servidor MetaTrader é obrigatório",
      "explanation": {
        "title": "Como encontrar seu<br>servidor {partnerShortName} MT4?",
        "description": "Vá para o Navegador > NomeDoCorretor > Contas"
      }
    },
    "mtLogin": {
      "label": "Login da conta",
      "mandatory": "Login MetaTrader é obrigatório",
      "explanation": {
        "title": "Como encontrar seu<br>login da conta MT4?",
        "description": "Vá para o Navegador > NomeDoCorretor > Contas"
      }
    },
    "mtInvestorPassword": {
      "label": "Senha do Investidor",
      "mandatory": "Senha é obrigatória",
      "explanation": {
        "title": "Como encontrar sua<br>senha do MT4?",
        "description": "Clique em Ferramentas > Opções > Servidor > Alterar Senha > Alterar senha de investidor (somente leitura)."
      },
      "disclaimer": "Importante: Certifique-se de inserir apenas a senha de investidor, e não a senha mestre ou de negociação. O Swiset requer apenas acesso de leitura e não se responsabiliza por problemas causados pelo fornecimento do tipo de senha incorreto."
    }
  },
  "formIssue": "Erro nos seus dados.\nPor favor, revise cada campo.",
  "commonIssue": "Ocorreu um erro.\nTente novamente.",
  "stepProgress": "<b>Passo {current}</b> de {total}",
  "validations": {
    "serverMandatory": "Servidor é obrigatório",
    "hostMandatory": "Host é obrigatório",
    "accountTypeMandatory": "Tipo de conta é obrigatório"
  }
  },
  "ko": {
  "serverLabel": "서버 선택",
  "hostLabel": "호스트",
  "accountTypeLabel": "계정 유형",
  "versionLabel": "MetaTrader 버전",
  "text1": "EA 없이 MetaTrader 계정을 동기화하세요",
  "text2": "MetaTrader 4 및 MetaTrader 5 자동 동기화 엔진 베타테스트에 참여하세요",
  "broughtToYou": "제공:",
  "submit": "계정 연결",
  "exnessDialog": {
    "title": "Exness 계정 감지됨",
    "message": "Exness 계정을 연결하려는 것으로 감지되었습니다. 더 나은 경험과 다양한 기능을 위해 전용 Exness 통합 페이지를 사용하는 것을 권장합니다.",
    "redirect": "Exness 통합 페이지로 이동",
    "continue": "MT5로 계속 진행"
  },
  "modal": {
    "success": {
      "title": "계정 동기화 완료",
      "text": "MetaTrader 계정이 성공적으로 동기화되었습니다"
    },
    "error": {
      "title": "오류",
      "text": "MetaTrader 계정을 연결하는 중 문제가 발생했습니다. 다시 시도하거나 고객 지원에 문의하세요."
    }
  },
  "steps": {
    "1": {
      "headOne": "몇 초 만에",
      "headTwo": "트레이딩 경험에 맞춤 설정",
      "btnContinue": "계속 »"
    },
    "2": {
      "headOne": "거의 완료되었습니다",
      "headTwo": "트레이딩을 한 단계 끌어올리세요",
      "btnBack": "« 뒤로",
      "btnContinue": "계속 »"
    },
    "3": {
      "headOne": "좋아요!",
      "headTwo": "{partnerShortName} 계정을 연결하고 동기화하세요",
      "chip": "거래는 자동으로 동기화됩니다",
      "btnContinue": "계정 연결 »"
    }
  },
  "categories": {
    "traderTypes": {
      "iDontKnow": "모르겠어요",
      "highFrequencyTrader": "고빈도 트레이더"
    },
    "experiences": {
      "learning": "트레이딩을 막 배우기 시작함",
      "demo": "데모 계정으로 트레이딩 중",
      "realNoProfit": "실계정에서 수익 없이 트레이딩 중",
      "profitSixMonths": "실계정에서 수익 있음 (6개월 미만)",
      "profitConsistent": "실계정에서 꾸준히 수익 있음 (6개월 이상)"
    }
  },
  "form": {
    "firstName": {
      "label": "이름이 무엇인가요?",
      "mandatory": "이름은 필수 항목입니다"
    },
    "lastName": {
      "label": "성을 입력해주세요",
      "mandatory": "성은 필수 항목입니다"
    },
    "username": {
      "label": "사용자 이름 만들기",
      "mandatory": "사용자 이름은 필수 항목입니다",
      "maxLength": "사용자 이름은 35자 미만이어야 합니다.",
      "not1": "'@'는 사용할 수 없습니다",
      "not2": "'&'는 사용할 수 없습니다",
      "not3": "공백은 사용할 수 없습니다",
      "existingIssue": "이미 존재하는 사용자 이름입니다.\n다른 이름을 시도해보세요."
    },
    "country": {
      "label": "거주하는 국가는 어디인가요?",
      "mandatory": "국가는 필수 항목입니다"
    },
    "typeOfTrading": {
      "label": "선호하는 트레이딩 유형은 무엇인가요?",
      "mandatory": "트레이딩 스타일은 필수 항목입니다"
    },
    "experience": {
      "label": "트레이딩 경험은 어떤가요?",
      "mandatory": "경험은 필수 항목입니다"
    },
    "mtServer": {
      "label": "{partnerShortName} MT4 서버 선택",
      "mandatory": "MetaTrader 서버는 필수 항목입니다",
      "explanation": {
        "title": "{partnerShortName} MT4 서버를 찾는 방법은?<br>",
        "description": "네비게이터 > 브로커 이름 > 계정"
      }
    },
    "mtLogin": {
      "label": "계정 로그인",
      "mandatory": "MetaTrader 로그인은 필수 항목입니다",
      "explanation": {
        "title": "MT4 계정 로그인을 찾는 방법은?<br>",
        "description": "네비게이터 > 브로커 이름 > 계정"
      }
    },
    "mtInvestorPassword": {
      "label": "투자자 비밀번호",
      "mandatory": "비밀번호는 필수 항목입니다",
      "explanation": {
        "title": "MT4 비밀번호를 찾는 방법은?<br>",
        "description": "도구 > 옵션 > 서버 > 비밀번호 변경 > 투자자(읽기 전용) 비밀번호 변경 클릭"
      },
      "disclaimer": "중요: 반드시 투자자 비밀번호만 입력하세요. 마스터 비밀번호나 거래 비밀번호는 입력하지 마세요. Swiset는 읽기 전용 접근만 필요하며, 잘못된 비밀번호 유형 제공으로 인한 문제에 대해 책임지지 않습니다."
    }
  },
  "formIssue": "입력한 정보에 오류가 있습니다.\n각 필드를 확인해주세요.",
  "commonIssue": "오류가 발생했습니다.\n다시 시도해주세요.",
  "stepProgress": "<b>{current}단계</b> / {total}",
  "validations": {
    "serverMandatory": "서버는 필수 항목입니다",
    "hostMandatory": "호스트는 필수 항목입니다",
    "accountTypeMandatory": "계정 유형은 필수 항목입니다"
  }
  },
  "th": {
  "serverLabel": "เลือกเซิร์ฟเวอร์",
  "hostLabel": "โฮสต์",
  "accountTypeLabel": "ประเภทบัญชี",
  "versionLabel": "เวอร์ชัน MetaTrader",
  "text1": "ซิงค์บัญชี MetaTrader ของคุณโดยไม่ต้องใช้ EA",
  "text2": "เข้าร่วมทดสอบเบต้าสำหรับระบบซิงค์อัตโนมัติของ MetaTrader 4 และ MetaTrader 5",
  "broughtToYou": "นำเสนอโดย",
  "submit": "เชื่อมต่อบัญชี",
  "exnessDialog": {
    "title": "ตรวจพบบัญชี Exness",
    "message": "เราตรวจพบว่าคุณกำลังพยายามเชื่อมต่อบัญชี Exness เพื่อประสบการณ์ที่ดีขึ้นและคุณสมบัติเพิ่มเติม เราแนะนำให้ใช้หน้าการรวม Exness โดยเฉพาะของเรา",
    "redirect": "ไปยังการรวม Exness",
    "continue": "ดำเนินการต่อด้วย MT5"
  },
  "modal": {
    "success": {
      "title": "ซิงค์บัญชีสำเร็จ",
      "text": "บัญชี MetaTrader ของคุณถูกซิงค์เรียบร้อยแล้ว"
    },
    "error": {
      "title": "ข้อผิดพลาด",
      "text": "เกิดปัญหาในการเชื่อมต่อบัญชี MetaTrader ของคุณ กรุณาลองอีกครั้งหรือ ติดต่อฝ่ายสนับสนุน"
    }
  },
  "steps": {
    "1": {
      "headOne": "อีกเพียงไม่กี่วินาที",
      "headTwo": "เพื่อปรับให้เหมาะกับประสบการณ์เทรดของคุณ",
      "btnContinue": "ดำเนินการต่อ »"
    },
    "2": {
      "headOne": "เกือบเสร็จแล้ว",
      "headTwo": "ยกระดับการเทรดของคุณ",
      "btnBack": "« กลับ",
      "btnContinue": "ดำเนินการต่อ »"
    },
    "3": {
      "headOne": "เยี่ยม!",
      "headTwo": "เชื่อมต่อและซิงค์บัญชี {partnerShortName} ของคุณ",
      "chip": "การซื้อขายจะถูกซิงค์โดยอัตโนมัติ",
      "btnContinue": "เชื่อมต่อบัญชี »"
    }
  },
  "categories": {
    "traderTypes": {
      "iDontKnow": "ไม่แน่ใจ",
      "highFrequencyTrader": "เทรดเดอร์ความถี่สูง"
    },
    "experiences": {
      "learning": "เพิ่งเริ่มเรียนรู้การเทรด",
      "demo": "เทรดด้วยบัญชีเดโม",
      "realNoProfit": "เทรดบัญชีจริงแต่ยังไม่มีผลกำไร",
      "profitSixMonths": "มีกำไรในบัญชีจริงน้อยกว่า 6 เดือน",
      "profitConsistent": "มีกำไรในบัญชีจริงมากกว่า 6 เดือน"
    }
  },
  "form": {
    "firstName": {
      "label": "ชื่อของคุณคืออะไร?",
      "mandatory": "ต้องระบุชื่อ"
    },
    "lastName": {
      "label": "นามสกุลของคุณคืออะไร?",
      "mandatory": "ต้องระบุนามสกุล"
    },
    "username": {
      "label": "สร้างชื่อผู้ใช้",
      "mandatory": "ต้องระบุชื่อผู้ใช้",
      "maxLength": "ชื่อผู้ใช้ต้องมีความยาวไม่เกิน 35 ตัวอักษร",
      "not1": "ไม่อนุญาตให้ใช้ '@'",
      "not2": "ไม่อนุญาตให้ใช้ '&'",
      "not3": "ไม่อนุญาตให้เว้นวรรค",
      "existingIssue": "ชื่อผู้ใช้นี้มีอยู่แล้ว\nกรุณาลองชื่ออื่น"
    },
    "country": {
      "label": "คุณอาศัยอยู่ที่ไหน?",
      "mandatory": "ต้องระบุประเทศ"
    },
    "typeOfTrading": {
      "label": "ประเภทการเทรดที่คุณชื่นชอบคืออะไร?",
      "mandatory": "ต้องระบุรูปแบบการเทรด"
    },
    "experience": {
      "label": "คุณมีประสบการณ์การเทรดอย่างไร?",
      "mandatory": "ต้องระบุประสบการณ์"
    },
    "mtServer": {
      "label": "เลือกเซิร์ฟเวอร์ {partnerShortName} MT4 ของคุณ",
      "mandatory": "ต้องระบุเซิร์ฟเวอร์ MetaTrader",
      "explanation": {
        "title": "จะหาเซิร์ฟเวอร์ {partnerShortName} MT4 ได้อย่างไร?<br>",
        "description": "ที่แถบนำทาง > ชื่อโบรกเกอร์ของคุณ > บัญชี"
      }
    },
    "mtLogin": {
      "label": "เข้าสู่ระบบบัญชี",
      "mandatory": "ต้องระบุรหัสเข้าสู่ระบบ MetaTrader",
      "explanation": {
        "title": "จะหารหัสบัญชี MT4 ได้อย่างไร?<br>",
        "description": "ที่แถบนำทาง > ชื่อโบรกเกอร์ของคุณ > บัญชี"
      }
    },
    "mtInvestorPassword": {
      "label": "รหัสผ่านนักลงทุน",
      "mandatory": "ต้องระบุรหัสผ่าน",
      "explanation": {
        "title": "จะหารหัสผ่าน MT4 ได้อย่างไร?<br>",
        "description": "คลิกที่ เครื่องมือ > ตัวเลือก > เซิร์ฟเวอร์ > เปลี่ยนรหัสผ่าน > เปลี่ยนเป็นรหัสผ่านนักลงทุน (เฉพาะอ่านอย่างเดียว)"
      },
      "disclaimer": "สำคัญ: โปรดป้อนเฉพาะรหัสผ่านนักลงทุน ห้ามใช้รหัสผ่านหลักหรือรหัสผ่านสำหรับการเทรด Swiset ต้องการสิทธิ์เข้าถึงแบบอ่านอย่างเดียวและไม่รับผิดชอบต่อปัญหาใดๆ ที่เกิดจากการป้อนรหัสผ่านที่ไม่ถูกต้อง"
    }
  },
  "formIssue": "ข้อมูลของคุณมีข้อผิดพลาด\nโปรดตรวจสอบแต่ละช่องอีกครั้ง",
  "commonIssue": "เกิดข้อผิดพลาด\nลองใหม่อีกครั้ง",
  "stepProgress": "<b>ขั้นตอนที่ {current}</b> จาก {total}",
  "validations": {
    "serverMandatory": "ต้องระบุเซิร์ฟเวอร์",
    "hostMandatory": "ต้องระบุโฮสต์",
    "accountTypeMandatory": "ต้องระบุประเภทบัญชี"
  }
  },
  "vi": {
  "serverLabel": "Chọn máy chủ",
  "hostLabel": "Máy chủ",
  "accountTypeLabel": "Loại tài khoản",
  "versionLabel": "Phiên bản MetaTrader",
  "text1": "Đồng bộ tài khoản MetaTrader của bạn mà không cần EA",
  "text2": "Tham gia thử nghiệm beta của chúng tôi cho hệ thống đồng bộ tự động MetaTrader 4 & 5",
  "broughtToYou": "Được mang đến bởi",
  "submit": "Kết nối tài khoản",
  "exnessDialog": {
    "title": "Phát hiện tài khoản Exness",
    "message": "Chúng tôi phát hiện bạn đang cố gắng kết nối tài khoản Exness. Để có trải nghiệm tốt hơn và nhiều tính năng hơn, hãy sử dụng trang tích hợp Exness chuyên dụng của chúng tôi.",
    "redirect": "Chuyển đến tích hợp Exness",
    "continue": "Tiếp tục với MT5"
  },
  "modal": {
    "success": {
      "title": "Đã đồng bộ tài khoản",
      "text": "Tài khoản MetaTrader của bạn đã được đồng bộ thành công"
    },
    "error": {
      "title": "Lỗi",
      "text": "Đã xảy ra sự cố khi kết nối tài khoản MetaTrader của bạn. Vui lòng thử lại hoặc liên hệ bộ phận hỗ trợ."
    }
  },
  "steps": {
    "1": {
      "headOne": "Chỉ vài giây để",
      "headTwo": "phù hợp với trải nghiệm giao dịch của bạn",
      "btnContinue": "Tiếp tục »"
    },
    "2": {
      "headOne": "Sắp xong rồi",
      "headTwo": "nâng cao giao dịch của bạn lên cấp độ cao hơn",
      "btnBack": "« Quay lại",
      "btnContinue": "Tiếp tục »"
    },
    "3": {
      "headOne": "Tuyệt vời!",
      "headTwo": "Kết nối & Đồng bộ tài khoản {partnerShortName} của bạn",
      "chip": "Giao dịch sẽ được đồng bộ tự động",
      "btnContinue": "Kết nối tài khoản »"
    }
  },
  "categories": {
    "traderTypes": {
      "iDontKnow": "Tôi không biết",
      "highFrequencyTrader": "Nhà giao dịch tần suất cao"
    },
    "experiences": {
      "learning": "Mới học giao dịch",
      "demo": "Giao dịch tài khoản demo",
      "realNoProfit": "Giao dịch tài khoản thực nhưng chưa có lợi nhuận",
      "profitSixMonths": "Có lợi nhuận dưới 6 tháng với tài khoản thực",
      "profitConsistent": "Có lợi nhuận ổn định hơn 6 tháng với tài khoản thực"
    }
  },
  "form": {
    "firstName": {
      "label": "Tên của bạn là gì?",
      "mandatory": "Tên là bắt buộc"
    },
    "lastName": {
      "label": "Họ của bạn là gì?",
      "mandatory": "Họ là bắt buộc"
    },
    "username": {
      "label": "Tạo tên người dùng",
      "mandatory": "Tên người dùng là bắt buộc",
      "maxLength": "Tên người dùng phải dưới 35 ký tự",
      "not1": "Không được sử dụng '@'",
      "not2": "Không được sử dụng '&'",
      "not3": "Không được có khoảng trắng",
      "existingIssue": "Tên người dùng đã tồn tại.\nVui lòng thử tên khác."
    },
    "country": {
      "label": "Bạn đang sống ở đâu?",
      "mandatory": "Quốc gia là bắt buộc"
    },
    "typeOfTrading": {
      "label": "Loại giao dịch yêu thích của bạn là gì?",
      "mandatory": "Kiểu giao dịch là bắt buộc"
    },
    "experience": {
      "label": "Kinh nghiệm giao dịch của bạn là gì?",
      "mandatory": "Kinh nghiệm là bắt buộc"
    },
    "mtServer": {
      "label": "Chọn máy chủ {partnerShortName} MT4 của bạn",
      "mandatory": "Máy chủ MetaTrader là bắt buộc",
      "explanation": {
        "title": "Làm thế nào để tìm máy chủ {partnerShortName} MT4 của bạn?<br>",
        "description": "Trên Navigator > Tên sàn của bạn > Tài khoản"
      }
    },
    "mtLogin": {
      "label": "Đăng nhập tài khoản",
      "mandatory": "Đăng nhập MetaTrader là bắt buộc",
      "explanation": {
        "title": "Làm thế nào để tìm đăng nhập tài khoản MT4 của bạn?<br>",
        "description": "Trên Navigator > Tên sàn của bạn > Tài khoản"
      }
    },
    "mtInvestorPassword": {
      "label": "Mật khẩu nhà đầu tư",
      "mandatory": "Mật khẩu là bắt buộc",
      "explanation": {
        "title": "Làm thế nào để tìm mật khẩu MT4 của bạn?<br>",
        "description": "Nhấp vào Công cụ > Tùy chọn > Máy chủ > Đổi mật khẩu > Đổi mật khẩu nhà đầu tư (chỉ đọc)"
      },
      "disclaimer": "Quan trọng: Vui lòng chỉ nhập mật khẩu nhà đầu tư, không phải mật khẩu chính hoặc mật khẩu giao dịch. Swiset chỉ cần quyền đọc và không chịu trách nhiệm với bất kỳ vấn đề nào phát sinh từ việc nhập sai loại mật khẩu."
    }
  },
  "formIssue": "Dữ liệu của bạn có lỗi.\nVui lòng kiểm tra lại từng trường.",
  "commonIssue": "Đã xảy ra lỗi.\nVui lòng thử lại.",
  "stepProgress": "<b>Bước {current}</b> trong tổng số {total}",
  "validations": {
    "serverMandatory": "Máy chủ là bắt buộc",
    "hostMandatory": "Máy chủ lưu trữ là bắt buộc",
    "accountTypeMandatory": "Loại tài khoản là bắt buộc"
  }
},
"ja": {
  "serverLabel": "サーバーを選択",
  "hostLabel": "ホスト",
  "accountTypeLabel": "アカウントタイプ",
  "versionLabel": "MetaTraderバージョン",
  "text1": "EAなしでMetaTraderアカウントを同期",
  "text2": "MetaTrader 4 & MetaTrader 5 自動同期エンジンのベータテストに参加",
  "broughtToYou": "提供：",
  "submit": "アカウントを接続",
  "exnessDialog": {
    "title": "Exnessアカウントが検出されました",
    "message": "Exnessアカウントを接続しようとしていることを検出しました。より良い体験とより多くの機能のために、専用のExness統合ページを使用することをお勧めします。",
    "redirect": "Exness統合に移動",
    "continue": "とにかくMT5で続行"
  },
  "modal": {
    "success": {
      "title": "アカウントが同期されました",
      "text": "MetaTraderアカウントが正常に同期されました"
    },
    "error": {
      "title": "エラー",
      "text": "MetaTraderアカウントの接続に問題がありました。再試行するかサポートにお問い合わせください。"
    }
  },
  "steps": {
    "1": {
      "headOne": "数秒で",
      "headTwo": "トレーダー体験をフィット",
      "btnContinue": "続行 »"
    },
    "2": {
      "headOne": "もうすぐ完了",
      "headTwo": "トレーディングをトップレベルに引き上げ",
      "btnBack": "« 戻る",
      "btnContinue": "続行 »"
    },
    "3": {
      "headOne": "素晴らしい！",
      "headTwo": "{partnerShortName}アカウントを接続＆同期",
      "chip": "取引は自動的に同期されます",
      "btnContinue": "アカウントを接続 »"
    }
  },
  "categories": {
    "traderTypes": {
      "iDontKnow": "わからない",
      "highFrequencyTrader": "高頻度トレーダー"
    },
    "experiences": {
      "learning": "トレーディングを学習中",
      "demo": "デモで取引",
      "realNoProfit": "リアルで利益なし",
      "profitSixMonths": "6ヶ月未満の利益",
      "profitConsistent": "6ヶ月以上の利益"
    }
  },
  "form": {
    "firstName": {
      "label": "名前は何ですか？",
      "mandatory": "名前は必須です"
    },
    "lastName": {
      "label": "苗字は何ですか？",
      "mandatory": "苗字は必須です"
    },
    "username": {
      "label": "ユーザー名を作成",
      "mandatory": "ユーザー名は必須です",
      "maxLength": "ユーザー名は35文字以下にしてください。",
      "not1": "'@'は使用できません",
      "not2": "'&'は使用できません",
      "not3": "スペースは使用できません",
      "existingIssue": "ユーザー名は既に存在します。\n別のものを試してください。"
    },
    "country": {
      "label": "どこに住んでいますか？",
      "mandatory": "国は必須です"
    },
    "typeOfTrading": {
      "label": "好きなトレーディングタイプは何ですか？",
      "mandatory": "トレーディングスタイルは必須です"
    },
    "experience": {
      "label": "トレーディング経験は何ですか？",
      "mandatory": "経験は必須です"
    },
    "mtServer": {
      "label": "{partnerShortName} MT4サーバーを選択",
      "mandatory": "MetaTraderサーバーは必須です",
      "explanation": {
        "title": "{partnerShortName} MT4サーバーを<br>見つける方法は？",
        "description": "ナビゲーター > ブローカー名 > アカウント"
      }
    },
    "mtLogin": {
      "label": "アカウントログイン",
      "mandatory": "MetaTraderログインは必須です",
      "explanation": {
        "title": "MT4アカウントログインを<br>見つける方法は？",
        "description": "ナビゲーター > ブローカー名 > アカウント"
      }
    },
    "mtInvestorPassword": {
      "label": "投資家パスワード",
      "mandatory": "パスワードは必須です",
      "explanation": {
        "title": "MT4パスワードを<br>見つける方法は？",
        "description": "ツール > オプション > サーバー > パスワード変更 > 投資家（読み取り専用）パスワードを変更。"
      },
      "disclaimer": "重要：投資家パスワードのみを入力してください。マスターパスワードやトレードパスワードではありません。Swisetは読み取り専用アクセスのみを必要とし、間違ったパスワードタイプの提供による問題については責任を負いません。"
    }
  },
  "formIssue": "データにエラーがあります。\n各フィールドを確認してください。",
  "commonIssue": "エラーが発生しました。\n再試行してください。",
  "stepProgress": "<b>ステップ {current}</b> / {total}",
  "validations": {
    "serverMandatory": "サーバーは必須です",
    "hostMandatory": "ホストは必須です",
    "accountTypeMandatory": "アカウントタイプは必須です"
  }
}

}
</i18n>